import {
  AlertCamera,
  AlertCameraServer,
  RealtimeAlertCamera,
  RealtimeAlertCameraServer,
} from 'features/cameras/cameras.types';
import { ENV } from 'shared/ENV';
import { isPhone } from 'shared/envUtils';
import { TFunction } from 'i18next';
import {
  CameraAttribution,
  CAMERA_ATTRIBUTION_TEXT,
} from './cameras.constants';

export const isTimelapseSupported = (camera: AlertCamera): boolean =>
  camera.provider === 'alertwest';

/**
 * Sort cameras by most recently moved first, nulls last.
 * Ties break on `id` for a stable order regardless of input. Does not mutate.
 */
export const sortCamerasByMostRecentMovement = <
  T extends Pick<AlertCamera, 'lastMovementAtTimestamp' | 'id'>,
>(
  cameras: T[],
): T[] =>
  [...cameras].sort((a, b) => {
    const diff =
      (b.lastMovementAtTimestamp?.getTime() ?? 0) -
      (a.lastMovementAtTimestamp?.getTime() ?? 0);
    return diff !== 0 ? diff : a.id.localeCompare(b.id);
  });

export const getCameraAttributionRichText = (camera: AlertCamera): string => {
  const attributionText =
    // @ts-expect-error it's OK for this not to exist
    CAMERA_ATTRIBUTION_TEXT[camera.attribution.toUpperCase()] || '';

  const cameraUrl = `${ENV.VITE_BASE_WEB_URL}camera/${camera.id}`;
  const attributionSuffix = attributionText ? ` - ${attributionText}` : '';

  return `<p><a href="${cameraUrl}">${camera.name}</a>${attributionSuffix}</p>`;
};

export const isMobileFullScreen = (fullScreen: boolean): boolean =>
  isPhone() && fullScreen;

export const getSimplifiedCameraNames = (
  cameras: AlertCamera[],
  t: TFunction,
): AlertCamera[] => {
  const defaultName = t('alertCameras.main');

  const firstCameraWords = cameras[0].name.split(' ');

  const commonCharacters = firstCameraWords.reduce((prefix, word) => {
    const newPrefix = prefix ? `${prefix} ${word}` : word;
    const isCommon = cameras.every((camera) =>
      camera.name.startsWith(newPrefix),
    );

    return isCommon ? newPrefix : prefix;
  }, '');

  if (commonCharacters.trim() === '') {
    return cameras;
  }

  const simplified = cameras.map((camera) => {
    const simpleName =
      camera.name.replace(commonCharacters, '').trim() || defaultName;
    return {
      ...camera,
      name: simpleName,
    };
  });

  if (simplified.filter((c) => c.name === defaultName).length > 1) {
    return cameras;
  }

  return simplified.map((camera) => ({
    ...camera,
    name: camera.name
      .replace('(', '')
      .replace(')', '')
      .replace('[', '')
      .replace(']', '')
      .trim(),
  }));
};

export const getRelatedCameras = (
  t: TFunction,
  camera?: AlertCamera,
  allCameras: AlertCamera[] = [],
): AlertCamera[] => {
  if (!camera) return [];
  if (allCameras.length === 0) return [];

  const relatedCameras = allCameras.filter((c) => {
    if (!c.cluster) return false;
    const cameraIdToMatch = camera.providerId;
    const relatedIds = c.cluster.relatedProviderIds;
    return relatedIds.includes(cameraIdToMatch);
  });

  return getSimplifiedCameraNames(relatedCameras, t).sort(
    (a: AlertCamera, b: AlertCamera) => a.name.localeCompare(b.name),
  );
};

export const getCameraAttributionImage = (
  camera: AlertCamera,
  imgUrls: Record<string, string>,
): string | null => {
  const attributionImageUrl = imgUrls[camera.attribution.toUpperCase()];

  if (attributionImageUrl) {
    return attributionImageUrl;
  }

  if (camera.hasPtz && camera.provider === 'alertwest') {
    return imgUrls.ALERTWEST;
  }

  return null;
};

export const getCameraAttributionLabelTransKey = (
  camera: AlertCamera,
): string => {
  const attributionToLabelTransKey: Record<CameraAttribution, string> = {
    UCSD: 'alertCameras.operatedBy',
    ALERTWEST: 'alertCameras.operatedBy',
    ALERTWILDFIRE: 'alertCameras.operatedBy',
    UO: 'alertCameras.operatedAndProvidedBy',
    UNR: 'alertCameras.operatedAndProvidedBy',
    HPWREN: 'alertCameras.operatedBy',
  };

  return (
    // @ts-expect-error it's OK for this not to exist
    attributionToLabelTransKey[camera.attribution] || 'alertCameras.operatedBy'
  );
};

export const transformAlertCameraServerData = (
  camera: AlertCameraServer,
): AlertCamera => ({
  ...camera,
  // `id` is the canonical client-side identifier, aliased to `compositeId`.
  id: camera.compositeId,
  imageTimestamp: new Date(camera.imageTimestamp),
  lastMovementAtTimestamp: camera.lastMovementAtTimestamp
    ? new Date(camera.lastMovementAtTimestamp)
    : null,
});

export const transformRealtimeAlertCameraServerData = (
  camera: RealtimeAlertCameraServer,
): RealtimeAlertCamera => ({
  ...camera,
  lastMovementAtTimestamp: camera.lastMovementAtTimestamp
    ? new Date(camera.lastMovementAtTimestamp)
    : null,
});
